import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import api from '../../api';
import { useLanguage } from '../../i18n/LanguageContext';
import './Activate.css';

const useQuery = () => new URLSearchParams(useLocation().search);

const Activate = () => {
    const { t, language, toggleLanguage } = useLanguage();
    const query = useQuery();
    const token = query.get('token') || '';
    const [password, setPassword] = useState('');
    const [confirm, setConfirm] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [resendEmail, setResendEmail] = useState('');
    const [resendLoading, setResendLoading] = useState(false);
    const [resendMessage, setResendMessage] = useState('');

    useEffect(() => {
        if (!token) setError(t('Invalid activation link'));
    }, [token]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (password.length < 8) {
            setError(t('Password must be at least 8 characters'));
            return;
        }
        if (password !== confirm) {
            setError(t('Passwords do not match'));
            return;
        }
        setLoading(true);
        setError('');
        try {
            const res = await api.activateAccount(token, password);
            if (res && res.data && res.data.token) {
                localStorage.setItem('tarewu_token', res.data.token);
                localStorage.setItem('tarewu_user', JSON.stringify(res.data.user));
                window.location.assign(res.data.user?.profile_completed ? '/dashboard' : '/profile');
            } else {
                setError(t('Activation failed'));
            }
        } catch (err) {
            console.error(err);
            // Show server-sent validation messages where possible
            if (err && err.message && err.message.includes('Invalid activation token')) {
                setError(t('Invalid activation token — the link may have expired. You can request a new activation link below.'));
            } else {
                setError(err.message || t('Activation error'));
            }
        } finally {
            setLoading(false);
        }
    };

    const handleResend = async () => {
        setResendMessage('');
        if (!resendEmail || !resendEmail.includes('@')) {
            setResendMessage(t('Please enter a valid email'));
            return;
        }
        setResendLoading(true);
        try {
            const res = await api.resendActivation(resendEmail);
            if (res && res.data) {
                setResendMessage(t('Activation link resent — check your email or SMS.'));
            } else {
                setResendMessage(t('Activation link resent (no preview available).'));
            }
        } catch (err) {
            console.error(err);
            setResendMessage(err.message || t('Failed to resend activation link'));
        } finally {
            setResendLoading(false);
        }
    };

    return (
        <div className="activate-page">
            <div className="card">
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                    <button
                        type="button"
                        onClick={toggleLanguage}
                        style={{ position: 'relative', background: 'rgba(0,0,0,0.06)', border: 'none', borderRadius: 8, padding: '6px 10px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
                    >
                        {language === 'en' ? 'EN' : 'SW'}
                    </button>
                </div>
                <h2>{t('Activate your account')}</h2>
                {error && <div className="error-message">{error}</div>}

                <form onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label>{t('New Password')}</label>
                        <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
                    </div>
                    <div className="form-group">
                        <label>{t('Confirm Password')}</label>
                        <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required />
                    </div>
                    <button className="btn btn-primary" type="submit" disabled={loading}>{loading ? t('Activating...') : t('Activate Account')}</button>
                </form>

                {/* Resend activation UI shown when token invalid or user requests it */}
                {(error && error.toLowerCase().includes('activation')) && (
                    <div className="resend-box">
                        <p>{t("Didn't receive a working activation link? Enter your email to resend.")}</p>
                        <div className="resend-row">
                            <input type="email" placeholder="you@example.com" value={resendEmail} onChange={(e) => setResendEmail(e.target.value)} />
                            <button className="btn" onClick={handleResend} disabled={resendLoading}>{resendLoading ? t('Sending...') : t('Resend')}</button>
                        </div>
                        {resendMessage && <div className={`resend-message ${resendMessage.toLowerCase().includes('failed') ? 'error' : 'success'}`}>{resendMessage}</div>}
                    </div>
                )}
            </div>
        </div>
    );
};

export default Activate;
